import {organizationApiClient} from 'organization/organizationApiClient'
import {useOrganization} from 'organization/providers/OrganizationProvider'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'

export type Invoice = {
  id: number
  number: string
  issued_at: Date

  provider_company_name: string
  provider_address: string
  provider_eik: string
  provider_vat_number: string
  provider_mol: string

  client_company_name: string
  client_address: string
  client_country: string
  client_eik: string
  client_vat_number: string
  client_mol: string

  vat_rate: number
  vat_reason: string

  subtotal: number
  vat: number
  total: number

  type: 'in' | 'out'

  pdf_path: string
  note: string
}

export interface OrganizationInvoicesContextProps {
  invoices: Invoice[]
  refresh: () => Promise<void>
}

export const OrganizationInvoicesContext = React.createContext<
  OrganizationInvoicesContextProps | undefined
>(undefined)

export default function OrganizationInvoicesProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const {organization} = useOrganization()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    const response = await apiClient.get(
      `api/organization/${organization?.id}/invoices`,
    )
    setInvoices(response)
    setLoading(false)
  }

  const refresh = async () => {
    await get()
  }

  return (
    <OrganizationInvoicesContext.Provider
      value={{
        invoices,
        refresh,
      }}
    >
      {loading && <FullPageLoader />}
      {!loading && props.children}
    </OrganizationInvoicesContext.Provider>
  )
}

export function useOrganizationInvoices() {
  const context = React.useContext(OrganizationInvoicesContext)
  if (context === undefined) {
    throw new Error(
      'useOrganizationInvoices must be used within OrganizationInvoicesProvider',
    )
  }
  return context
}
